import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem,
  Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, Chip,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import api, { clientAPI, invoiceAPI, marcheAPI, proformaAPI, settingsAPI, stockAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { apiError, fcfa, today } from '../utils/format';

const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const pct = (v) => Number(String(v ?? '').replace(',', '.')) || 0;

/** Même règle que le serveur : remise sur le HT de chaque taux, puis TVA par taux sur le HT remisé */
function totals(lines, remisePct) {
  const byRate = {};
  let brut = 0;
  for (const l of lines) {
    const lh = (parseInt(l.quantite, 10) || 0) * (parseInt(l.prix_unitaire_ht, 10) || 0);
    brut += lh;
    byRate[l.taux_tva] = (byRate[l.taux_tva] || 0) + lh;
  }
  let remise = 0;
  let tva = 0;
  for (const [r, v] of Object.entries(byRate)) {
    const rem = Math.round((v * remisePct) / 100);
    remise += rem;
    tva += Math.round(((v - rem) * Number(r)) / 100);
  }
  const ht = brut - remise;
  return { brut, remise, ht, tva, ttc: ht + tva };
}

/** Formulaire commun : facture (kind="facture") ou facture proforma / devis (kind="proforma"). */
const FactureForm = ({ kind = 'facture' }) => {
  const isPf = kind === 'proforma';
  const docAPI = isPf ? proformaAPI : invoiceAPI;
  const base = isPf ? '/proformas' : '/factures';
  const { id } = useParams();
  const [params] = useSearchParams();
  const { canValiderFactures } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [clients, setClients] = useState([]);
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({ client_id: '', date_facture: today(), date_echeance: '', objet: '', conditions: '', remise_pct: '', delivery_ids: [], ...(isPf ? {} : { marche_id: '' }) });
  const [marches, setMarches] = useState([]); // BC du client (factures)
  const [echeanceTouched, setEcheanceTouched] = useState(false);
  const [lines, setLines] = useState([]);
  const [bls, setBls] = useState([]);
  const [warning, setWarning] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [newClient, setNewClient] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, c, t] = await Promise.all([settingsAPI.get(), clientAPI.list(), stockAPI.getPbaTypes()]);
        setSettings(s.data); setClients(c.data); setTypes(t.data);
        const tva = s.data.taux_tva;
        if (id) {
          const { data: inv } = await docAPI.get(id);
          if (inv.statut !== 'BROUILLON') { navigate(`${base}/${id}`, { replace: true }); return; }
          setForm({
            client_id: inv.client_id,
            date_facture: String(isPf ? inv.date_proforma : inv.date_facture).slice(0, 10),
            date_echeance: String(isPf ? inv.date_validite : inv.date_echeance).slice(0, 10),
            objet: inv.objet || '', conditions: inv.conditions || '',
            remise_pct: inv.remise_pct ? String(inv.remise_pct).replace('.', ',') : '',
            delivery_ids: isPf ? [] : inv.bons_livraison.map((b) => b.id),
            ...(isPf ? {} : { marche_id: inv.marche_id || '' }),
          });
          setEcheanceTouched(true);
          if (!isPf) setBls(inv.bons_livraison);
          setLines(inv.lines.map((l) => ({ ...l })));
        } else if (!isPf && params.get('bl')) {
          const ids = params.get('bl').split(',').map(Number);
          const { data: p } = await invoiceAPI.proposal(ids);
          setForm((f) => ({ ...f, client_id: p.client_id || '', objet: p.objet, delivery_ids: p.delivery_ids, marche_id: p.marche_id || '' }));
          setLines(p.lines);
          setBls(p.bons_livraison);
          if (!p.client_id) setWarning(`Le BL a été établi pour « ${p.client_name} » sans fiche client : choisissez ou créez le client.`);
          if (p.prix_manquants.length) setWarning((w) => `${w ? `${w} ` : ''}Prix non renseigné pour : ${p.prix_manquants.join(', ')} (Paramètres société).`);
        } else {
          setLines([{ code: '01', pba_type_id: '', designation: '', quantite: 1, prix_unitaire_ht: 0, taux_tva: tva }]);
        }
      } catch (err) {
        setError(apiError(err));
      }
    })();
  }, [id, params, navigate, isPf, docAPI, base]);

  // BC ouverts du client (facture) — le BC des BL est repris automatiquement
  useEffect(() => {
    if (isPf || !form.client_id) { setMarches([]); return; }
    marcheAPI.choix(form.client_id).then((r) => setMarches(r.data.marches)).catch(() => setMarches([]));
  }, [form.client_id, isPf]);

  // Échéance automatique : date + délai de paiement (30 jours par défaut)
  useEffect(() => {
    if (settings && !echeanceTouched && form.date_facture) {
      setForm((f) => ({ ...f, date_echeance: addDays(f.date_facture, isPf ? 30 : settings.delai_paiement_jours) }));
    }
  }, [form.date_facture, settings, echeanceTouched, isPf]);

  const remisePct = pct(form.remise_pct);
  const remiseOk = remisePct >= 0 && remisePct < 100;
  const t = useMemo(() => totals(lines, remiseOk ? remisePct : 0), [lines, remisePct, remiseOk]);

  const setLine = (i, patch) => setLines((prev) => prev.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  // Choix d'un type : désignation et prix de vente par défaut (catalogue) repris automatiquement
  const pickType = (i, typeId) => {
    const type = types.find((x) => String(x.id) === String(typeId));
    setLine(i, { pba_type_id: typeId, designation: type?.designation || type?.code || '', prix_unitaire_ht: Number(type?.prix_ht) || 0 });
  };
  const [info, setInfo] = useState('');
  const saveDefaultPrice = async (typeId, prix) => {
    try {
      const r = await invoiceAPI.setDefaultPrice(typeId, parseInt(prix, 10) || 0);
      setTypes((prev) => prev.map((x) => (x.id === r.data.type.id ? { ...x, prix_ht: r.data.type.prix_ht } : x)));
      setInfo(r.data.message);
    } catch (e) { setError(apiError(e)); }
  };
  const addLine = () => setLines((prev) => [...prev, {
    code: String(prev.length + 1).padStart(2, '0'), pba_type_id: '', designation: '', quantite: 1,
    prix_unitaire_ht: 0, taux_tva: settings?.taux_tva ?? 18,
  }]);

  const save = async (emit) => {
    setBusy(true); setError('');
    try {
      const payload = {
        ...form,
        ...(isPf ? { date_proforma: form.date_facture, date_validite: form.date_echeance } : {}),
        remise_pct: form.remise_pct === '' ? 0 : form.remise_pct,
        lines: lines.map((l, i) => ({ ...l, code: l.code || String(i + 1).padStart(2, '0'), pba_type_id: l.pba_type_id || null })),
      };
      const r = id ? await docAPI.update(id, payload) : await docAPI.create(payload);
      const docId = (isPf ? r.data.proforma : r.data.invoice).id;
      if (emit === 'emettre') await docAPI.emit(docId);
      if (emit === 'demander') {
        const v = await api.post(`/${isPf ? 'proformas' : 'invoices'}/${docId}/demander-validation`);
        navigate(`${base}/${docId}`, { state: { info: v.data.message } });
        return;
      }
      navigate(`${base}/${docId}`);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const createClient = async () => {
    try {
      const r = await clientAPI.create(newClient);
      setClients((prev) => [...prev, r.data].sort((a, b) => a.nom.localeCompare(b.nom)));
      setForm((f) => ({ ...f, client_id: r.data.id }));
      setNewClient(null);
    } catch (err) {
      setError(apiError(err));
    }
  };

  if (!settings) return <Layout title={isPf ? 'Proforma' : 'Facture'}>{error ? <Alert severity="error">{error}</Alert> : 'Chargement…'}</Layout>;

  return (
    <Layout title={id ? 'Modifier le brouillon' : isPf ? 'Nouvelle facture proforma (devis)' : 'Nouvelle facture'}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}
      {warning && <Alert severity="warning" sx={{ mb: 2 }}>{warning}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={5}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField select fullWidth size="small" label="Client *" value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
              {clients.map((c) => <MenuItem key={c.id} value={c.id}>{c.nom}{c.ville ? ` — ${c.ville}` : ''}</MenuItem>)}
            </TextField>
            <Button sx={{ whiteSpace: 'nowrap', flexShrink: 0 }} onClick={() => setNewClient({ nom: '', adresse: '', ville: '', ninea: '' })}>+ Client</Button>
          </Box>
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField fullWidth size="small" type="date" label="Date" InputLabelProps={{ shrink: true }}
            value={form.date_facture} onChange={(e) => setForm({ ...form, date_facture: e.target.value })} />
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField fullWidth size="small" type="date" label={isPf ? 'Valable jusqu\'au' : 'Échéance'} InputLabelProps={{ shrink: true }}
            value={form.date_echeance} onChange={(e) => { setEcheanceTouched(true); setForm({ ...form, date_echeance: e.target.value }); }} />
        </Grid>
        <Grid item xs={12} md={3}>
          {!isPf && (marches.length > 0 || form.marche_id) && (
            <TextField select fullWidth size="small" label="Bon de commande client (BC)" sx={{ mb: 1 }} value={form.marche_id || ''}
              disabled={form.delivery_ids.length > 0 && !!form.marche_id}
              helperText={form.delivery_ids.length > 0 && form.marche_id ? 'BC des BL facturés' : 'Pour le suivi du marché'}
              onChange={(e) => setForm({ ...form, marche_id: e.target.value })}>
              <MenuItem value="">— Aucun —</MenuItem>
              {form.marche_id && !marches.some((m) => m.id === form.marche_id) && <MenuItem value={form.marche_id}>BC (clos)</MenuItem>}
              {marches.map((m) => <MenuItem key={m.id} value={m.id}>{m.libelle}</MenuItem>)}
            </TextField>
          )}
          {form.delivery_ids.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: '100%' }}>BL rattachés</Typography>
              {bls.map((b) => <Chip key={b.id} size="small" label={b.bl_numero} />)}
            </Box>
          )}
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth size="small"
            label={isPf ? 'Objet (ex. Fourniture de poteaux — chantier de Mbour)' : 'Objet (1re ligne de la désignation, ex. FACTURE N°08 BC 2026106485 du 10/08/2026)'}
            value={form.objet} onChange={(e) => setForm({ ...form, objet: e.target.value })} />
        </Grid>
        {isPf && (
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Conditions (délai de livraison, modalités de paiement…)"
              value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} />
          </Grid>
        )}
      </Grid>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={70}>Code</TableCell>
              <TableCell width={170}>Type PBA</TableCell>
              <TableCell>Désignation</TableCell>
              <TableCell width={90} align="right">Qté</TableCell>
              <TableCell width={130} align="right">Prix HT</TableCell>
              <TableCell width={90} align="right">TVA %</TableCell>
              <TableCell width={130} align="right">Total TTC</TableCell>
              <TableCell width={40} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((l, i) => {
              const ht = (parseInt(l.quantite, 10) || 0) * (parseInt(l.prix_unitaire_ht, 10) || 0);
              return (
                <TableRow key={i}>
                  <TableCell><TextField variant="standard" value={l.code || ''} onChange={(e) => setLine(i, { code: e.target.value })} /></TableCell>
                  <TableCell>
                    <TextField select variant="standard" fullWidth value={l.pba_type_id || ''} onChange={(e) => pickType(i, e.target.value)}
                      SelectProps={{ displayEmpty: true, renderValue: (v) => types.find((x) => String(x.id) === String(v))?.code || '— Autre —' }}>
                      <MenuItem value="">— Autre —</MenuItem>
                      {types.map((ty) => (
                        <MenuItem key={ty.id} value={ty.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
                          <span>{ty.code}</span>
                          <span style={{ color: Number(ty.prix_ht) ? '#5B6675' : '#B7791F', fontSize: 13 }}>{Number(ty.prix_ht) ? `${fcfa(ty.prix_ht)} F` : 'prix à définir'}</span>
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell><TextField variant="standard" fullWidth value={l.designation} onChange={(e) => setLine(i, { designation: e.target.value })} /></TableCell>
                  <TableCell><TextField variant="standard" type="number" inputProps={{ min: 1, style: { textAlign: 'right' } }} value={l.quantite} onChange={(e) => setLine(i, { quantite: e.target.value })} /></TableCell>
                  <TableCell>
                    <TextField variant="standard" type="number" inputProps={{ min: 0, style: { textAlign: 'right' } }} value={l.prix_unitaire_ht} onChange={(e) => setLine(i, { prix_unitaire_ht: e.target.value })} />
                    {(() => {
                      const ty = l.pba_type_id && types.find((x) => String(x.id) === String(l.pba_type_id));
                      if (!ty) return null;
                      const prix = parseInt(l.prix_unitaire_ht, 10) || 0;
                      const defaut = Number(ty.prix_ht) || 0;
                      if (prix === defaut && defaut) return null;
                      return (
                        <Box sx={{ fontSize: 11.5, mt: 0.25, textAlign: 'right', color: defaut ? 'text.secondary' : 'warning.main' }}>
                          {defaut ? `Défaut : ${fcfa(defaut)}` : 'Pas de prix par défaut'}
                          {prix > 0 && <Box component="button" type="button" onClick={() => saveDefaultPrice(ty.id, prix)}
                            sx={{ all: 'unset', cursor: 'pointer', color: 'primary.main', fontWeight: 600, display: 'block', '&:hover': { textDecoration: 'underline' } }}>
                            Enregistrer {fcfa(prix)} par défaut
                          </Box>}
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell><TextField variant="standard" type="number" inputProps={{ min: 0, max: 100, style: { textAlign: 'right' } }} value={l.taux_tva} onChange={(e) => setLine(i, { taux_tva: e.target.value })} /></TableCell>
                  <TableCell align="right">{fcfa(Math.round(ht * (1 + Number(l.taux_tva || 0) / 100)))}</TableCell>
                  <TableCell><IconButton size="small" onClick={() => setLines(lines.filter((_, j) => j !== i))} disabled={lines.length === 1}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Box sx={{ p: 1 }}><Button startIcon={<AddIcon />} onClick={addLine}>Ajouter une ligne</Button></Box>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 3, mt: 2, flexWrap: 'wrap' }}>
        <TextField size="small" label="Remise %" value={form.remise_pct} sx={{ width: 110 }} InputLabelProps={{ shrink: true }} placeholder="0"
          error={!remiseOk} helperText={remiseOk ? ' ' : 'De 0 à 99,99'}
          inputProps={{ inputMode: 'decimal' }} onChange={(e) => setForm({ ...form, remise_pct: e.target.value })} />
        {t.remise > 0 && (
          <>
            <Typography>Montant brut HT : <strong>{fcfa(t.brut)}</strong></Typography>
            <Typography color="error.main">Remise {String(remisePct).replace('.', ',')} % : <strong>−{fcfa(t.remise)}</strong></Typography>
          </>
        )}
        <Typography>Total HT : <strong>{fcfa(t.ht)}</strong></Typography>
        <Typography>TVA : <strong>{fcfa(t.tva)}</strong></Typography>
        <Typography variant="h6">Total TTC : {fcfa(t.ttc)} FCFA</Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button onClick={() => navigate(-1)}>Annuler</Button>
        <Button variant="outlined" disabled={busy || !form.client_id || !remiseOk} onClick={() => save(false)}>Enregistrer le brouillon</Button>
        {canValiderFactures ? (
          <Button variant="contained" disabled={busy || !form.client_id || t.ttc <= 0 || !remiseOk}
            onClick={() => window.confirm(`Valider et émettre ${isPf ? 'la proforma' : 'la facture'} ? Elle recevra son numéro définitif et ne pourra plus être modifiée.`) && save('emettre')}>
            Enregistrer, valider et émettre
          </Button>
        ) : (
          <Button variant="contained" disabled={busy || !form.client_id || t.ttc <= 0 || !remiseOk} onClick={() => save('demander')}>
            Enregistrer et demander la validation
          </Button>
        )}
      </Box>

      <Dialog open={!!newClient} onClose={() => setNewClient(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Nouveau client</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          {newClient && ['nom', 'adresse', 'ville', 'ninea'].map((k) => (
            <TextField key={k} fullWidth size="small" sx={{ mb: 2 }} label={{ nom: 'Nom *', adresse: 'Adresse', ville: 'Ville', ninea: 'NINEA' }[k]}
              value={newClient[k]} onChange={(e) => setNewClient({ ...newClient, [k]: e.target.value })} />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewClient(null)}>Fermer</Button>
          <Button variant="contained" onClick={createClient} disabled={!newClient?.nom}>Créer</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default FactureForm;
